import { useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Loader, Center, Text } from "@mantine/core";
import { AuthContext } from "../services/context";
import { getCurrentWorkspace } from "../services/userConfig";
import PDFBuilder from "./PDFBuilder";
import { useGetDocumentTemplate, useUpdateDocumentTemplate } from "../api/user";
import { useFileUpload } from "../services/react-query";

function EditDocumentGenerator() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const eventId = currentWorkspace.eventId;

  const {
    data: documentData,
    isPending,
    isError,
    error,
  } = useGetDocumentTemplate(id);
  const { mutate: updateTemplate } = useUpdateDocumentTemplate({ id });
  const { mutateAsync: uploadFile } = useFileUpload();

  const handleCancel = () => {
    navigate("/app/documents?tab=templates");
  };

  const handleSubmit = async (builderData) => {
    // builderData includes: { docFormat, bgImage, bgFile, placeholders }
    let backgroundKey;
    let backgroundChanged = false;

    if (builderData.bgFile) {
      const uploadResult = await uploadFile(builderData.bgFile);
      backgroundKey = uploadResult.fileKey;
      backgroundChanged = true;
    } else if (!builderData.bgImage) {
      backgroundKey = null;
      backgroundChanged = true;
    }

    const formattedData = {
      name: documentData.name, // Keep existing name
      documentData: {
        documentSize: `${builderData.docFormat.widthMm}mm x ${builderData.docFormat.heightMm}mm`,
        hasBackground: !!builderData.bgImage,
        fields: builderData.placeholders.map((p) => ({
          name: p.name,
          xPct: parseFloat(p.xPct.toFixed(2)),
          yPct: parseFloat(p.yPct.toFixed(2)),
          widthPct: parseFloat(p.widthPct.toFixed(2)),
          heightPct: parseFloat(p.heightPct.toFixed(2)),
          fontSize: p.fontSize,
          color: p.color,
          align: p.align,
        })),
      },
    };

    if (backgroundChanged) {
      formattedData.backgroundKey = backgroundKey;
    }

    updateTemplate(
      {
        ...formattedData,
      },
      {
        onSuccess: () => {
          navigate("/app/documents?tab=templates");
        },
      }
    );
  };

  if (isPending) {
    return (
      <Center style={{ height: "calc(100vh - 100px)" }}>
        <Loader size="xl" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Center style={{ height: "calc(100vh - 100px)" }}>
        <Text c="red">{error.message}</Text>
      </Center>
    );
  }

  // Parse docSize string to width and height
  let widthMm = 210;
  let heightMm = 297;
  if (documentData?.documentData?.documentSize) {
    const parts = documentData.documentData.documentSize
      .split("x")
      .map((s) => s.trim().toLowerCase());
    if (parts.length === 2 && parts[0].endsWith("mm")) {
      widthMm = parseFloat(parts[0].replace("mm", ""));
      heightMm = parseFloat(parts[1].replace("mm", ""));
    }
  }

  const initialValue = {
    docFormat: { widthMm, heightMm },
    bgImage: documentData?.backgroundKey || null,
    placeholders:
      documentData?.documentData?.fields?.map((f, index) => ({
        ...f,
        id: `field-${index}`, // Need to inject an ID for internal PDFBuilder state
      })) || [],
  };

  const availablePlaceholders = [
    { value: "fullName", label: "Full Name" },
    { value: "firstName", label: "First Name" },
    { value: "lastName", label: "Last Name" },
    { value: "date", label: "Date" },
    { value: "eventName", label: "Event Name" },
  ];

  return (
    <Container fluid p={0}>
      <PDFBuilder
        initialValue={initialValue}
        availablePlaceholders={availablePlaceholders}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </Container>
  );
}

export default EditDocumentGenerator;
